//Libraries
import {FC, useEffect, useState} from 'react';

//Typing
import {Draft} from 'src/types/common';
import {IAIModel, IAIRecord} from 'src/types/org/ai/aiInterface';
import {IOpsRequest} from 'src/types/org/operation/requestInterface';

//Components
import {ContainerInline, ErrorNotice, HeaderInline, Loader} from 'src/generic/component';
import {ErrorManager, ERRORS_EMPTY, ValidationError} from "../../../types";
import {OrgFields} from "./orgFields";
import {useAuth} from "../../../contexts";
import {IOrgModel} from "../../../types/org/orgInterface";
import {Button, PrimaryButton} from "@cb/apricot-react";
import {AiFields} from "./aiFields";
import {validate} from "../../../types/org/ai";
import {API} from "../../../apis/apiWrapper";
import {processResponse} from "../../../utils/api";
import {Dialog} from "@mui/material";
import {disableDismiss, enableDismiss} from "../../../generic/utils/modal";

interface IProfileEditFormProps {
    record: IAIRecord;
    onComplete: () => void;
    onClose: () => void;
}

export const ProfileEditForm: FC<IProfileEditFormProps> = ({record, onComplete, onClose}) => {

    const { lastName, firstName,roles} = useAuth();
    const isAdmin = roles.includes('XPM_ADMIN');
    const allowProtected = isAdmin;
    // Post okta migrations, userNames now start with 'Z9Z9P',replacing it with lastName, firstName
    const displayUserName = lastName + ', ' + firstName;
    const preparedByUser = displayUserName;
    const approvedByUser = displayUserName;
    // @ts-ignore
    const type = (record.ai.aiCategoryCd === '1' ? 'ai' : 'aia') || 'ai';

    const [org, setOrg] = useState<Draft<IOrgModel>>({});
    const [ai, setAi] = useState<Draft<IAIModel>>({});
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>('');
    const [errorMessageOpen, setErrorMessageOpen] = useState<boolean>(false);
    const showLoading = submitting;

    let errors = ErrorManager(ERRORS_EMPTY);

    useEffect(() => {
        const data = record;
        setOrg(data?.org || {});
        setAi(data?.ai || {});
    }, [record]);

    if (showLoading) {
        return <Loader/>;
    } else {
        let request: IOpsRequest<IAIRecord> | undefined = undefined;
        let validated: boolean = false;
        try {
            const data = validate({
                org: org,
                ai: ai
            }, true);
            request = {
                request: data,
                submitted: {}, //No user submission for this update
                original: record,
                meta: {
                    preparedBy: preparedByUser,
                    approvedBy: approvedByUser
                }
            };
            validated = true;
        } catch (error: any) {
            if (error instanceof ValidationError) {
                errors = ErrorManager(error.fieldErrors);
            }
        }
        //Submit Function
        const submit = async () => {
            setSubmitting(true);
            try {
                console.log('profileEditForm:submit call: request is :' + JSON.stringify(request));
                if (validated) {
                    const response: IOrgModel = await API.post('AiFormRequest', `/save?key=saveProfile`, {
                        body: {
                            ...request
                        }
                    }).then((response: any) => processResponse(response));

                    onComplete();
                } else {
                    throw new Error("The save failed as some of the entered fields are invalid.");
                }
            } catch (error: any) {
                setErrorMessage(error?.message || error);
                setErrorMessageOpen(true);
                console.log(error);
            }
            // Perform Redirect
            setSubmitting(false);
        };
        //End of Submit

        if (['ai', 'aia'].includes(type)) {
            return <>

                <Dialog fullWidth={true} maxWidth={'md'} open={showLoading} onClose={disableDismiss}>
                    <div style={{padding: '20px'}}>
                        <Loader/>
                    </div>
                </Dialog>

                {errorMessage &&
                    <div style={{marginBottom: '20px', marginTop: '20px'}}>
                        <ErrorNotice errorMessage={errorMessage}/>
                    </div>
                }
                <Dialog fullWidth={true} maxWidth={'md'} open={errorMessageOpen}
                        onClose={enableDismiss((event, reason) => {
                            setErrorMessageOpen(false);
                        })}>
                    <ContainerInline style={{padding: '20px'}}>
                        <HeaderInline style={{marginBottom: '6px'}}>
                            <i style={{marginRight: '10px'}}
                               className="cb-icon cb-icon-circular cb-exclamation cb-red1-color" aria-hidden="true"></i>
                            <h4 className="cb-notification-title"><strong>Errors Occurred</strong></h4>
                        </HeaderInline>
                        <div style={{marginLeft: '42px'}} className="cb-notification-content">
                            {errorMessage}
                        </div>
                    </ContainerInline>
                </Dialog>
                <OrgFields
                    item={org}
                    type="ai"
                    original={record.org || undefined}
                    defaults={{}}
                    hints={{}}
                    errors={errors.getFieldError('org')}
                    showProtected={allowProtected}
                    onUpdate={(data) => {
                        setOrg(data);
                    }}></OrgFields>

                <AiFields
                    item={ai}
                    original={record.ai || undefined}
                    errors={errors.getFieldError('ai')}
                    showProtected={allowProtected}
                    onUpdate={(data) => {
                        setAi(data);
                    }}></AiFields>

                <br/>

                <PrimaryButton style={{width: '100%', marginBottom: '10px'}} onClick={() => {
                    submit();
                }} disabled={submitting}>Submit</PrimaryButton>
                <Button style={{marginBottom: '10px', width: '100%'}} onClick={() => {
                    onClose();
                }}>Close</Button>
            </>;
        } else {
            return <ErrorNotice errorMessage={`unable to load form.`}/>;
        }
    }
};